import { Typography } from '@/components/atoms/Typography';
import { AppointmentConfirmedTickIcon } from '@/components/atoms/icons';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import React, { useEffect, useRef } from 'react';
import { Modal, StyleSheet, View } from 'react-native';

export interface AppointmentSuccessDialogProps {
    visible: boolean;
    /** Title text; defaults to "Appointment Confirmed" */
    title?: string;
    /** Description text */
    message?: string;
    /** Auto-close delay in ms; defaults to 3000 */
    autoCloseDelayMs?: number;
    onAutoClose: () => void;
}

export const AppointmentSuccessDialog: React.FC<AppointmentSuccessDialogProps> = ({
    visible,
    title = 'Appointment Confirmed',
    message = 'Your appointment has been successfully Reschedule.',
    autoCloseDelayMs = 3000,
    onAutoClose,
}) => {
    const colors = useUIColors();
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const viewTrackedRef = useRef(false);

    useEffect(() => {
        if (!visible) {
            viewTrackedRef.current = false;
            return;
        }
        if (viewTrackedRef.current) {
            return;
        }
        viewTrackedRef.current = true;
        trackCatalogEvent({
            screen: ALMETLAA_SCREEN.APPOINTMENT,
            actionName: ALMETLAA_ACTION.APPOINTMENT.CONFIRMED_DIALOG_VIEW,
            eventType: 'view',
        });
    }, [visible]);

    // Auto-close behaviour
    useEffect(() => {
        if (!visible) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            return;
        }

        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            onAutoClose();
        }, autoCloseDelayMs);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [visible, autoCloseDelayMs, onAutoClose]);

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onAutoClose}>
            <View style={styles.overlay}>
                <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
                    {/* Check Icon */}
                    <View style={styles.iconContainer}>
                        <AppointmentConfirmedTickIcon size={56} />
                    </View>

                    {/* Title */}
                    <Typography
                        variant="headline"
                        color="primary"
                        style={styles.title}
                    >
                        {title}
                    </Typography>

                    {/* Description */}
                    <Typography
                        variant="body"
                        color="secondary"
                        style={styles.message}
                    >
                        {message}
                    </Typography>
                </View>
            </View>
        </Modal>
    );
};


const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: UI_PRIMITIVES.overlayBackdrop,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
    },
    container: {
        borderRadius: 16,
        paddingVertical: 32,
        paddingHorizontal: 24,
        width: '100%',
        maxWidth: 340,
        alignItems: 'center',
        gap: 16,
    },
    iconContainer: {
        marginBottom: 4,
    },
    title: {
        textAlign: 'center',
        width: '100%',
    },
    message: {
        textAlign: 'center',
        fontSize: 14,
        lineHeight: 21,
        width: '100%',
    },
});

export default AppointmentSuccessDialog;
